import { FormEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './organization-editor.css';
import {
  ORGANIZATION_NAME_MAX_LENGTH,
  ORGANIZATION_NAME_MIN_LENGTH,
} from '../../../constants/organization';
import {
  organizationsListPath,
  organizationTeamPath,
  ROUTES,
} from '../../../constants/routes';
import {
  useCreateOrganizationMutation,
  useOrganizationQuery,
  useUpdateOrganizationMutation,
} from '../../../hooks/useDashboardQueries';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatDate } from '../../../shared/utils/dateFormat';
import { OrganizationV2, OwnerCandidate } from '../../../types/organization';
import { formatOwnerLabel } from '../../../utils/formatOwnerLabel';
import { parseApiError } from '../../../utils/parseApiError';
import { ErrorMessage } from '../../../components/common/ErrorMessage/ErrorMessage';
import { MobileBottomNav } from '../Stations/MobileBottomNav';
import { OwnerUserSearch } from './OwnerUserSearch';
import { organizationPremiumPath } from './OrganizationsTable';

/**
 * Create (`/organizations/new`) and edit (`/organizations/:id/edit`) page.
 * Edit loads the organization, then mounts the form keyed by id so its state
 * initializes from the loaded data once.
 */
export const OrganizationEditor = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId: string }>();
  const isEdit = !!organizationId;
  const orgQ = useOrganizationQuery(organizationId);
  const backToList = () => navigate(organizationsListPath());

  let body;
  if (!isEdit) {
    body = <OrganizationForm organization={null} onDone={backToList} />;
  } else if (orgQ.error) {
    body = (
      <ErrorMessage
        message={parseApiError(orgQ.error, t('dashboard.organizations.errorLoading')).message}
        onRetry={() => void orgQ.refetch()}
      />
    );
  } else if (!orgQ.data) {
    body = <div className="org-editor-loading">{t('dashboard.common.loading')}</div>;
  } else {
    body = (
      <OrganizationForm
        key={orgQ.data.id}
        organization={orgQ.data}
        onDone={backToList}
      />
    );
  }

  return (
    <div className="org-editor-page">
      <div className="org-editor-container">
        <header className="org-editor-header">
          <button
            type="button"
            className="org-editor-back"
            onClick={backToList}
            aria-label={t('dashboard.organizations.backToList')}
          >
            <span className="material-symbols-outlined" aria-hidden="true">arrow_back</span>
          </button>
          <div className="org-editor-mark" aria-hidden="true">
            <span className="material-symbols-outlined">corporate_fare</span>
          </div>
          <div className="org-editor-heading">
            <span className="org-editor-eyebrow">
              {isEdit
                ? t('dashboard.organizations.editTitle')
                : t('dashboard.organizations.createTitle')}
            </span>
            <h1 className="org-editor-title">
              {isEdit ? orgQ.data?.name ?? '…' : t('dashboard.organizations.create')}
            </h1>
            {orgQ.data && <OrganizationMeta organization={orgQ.data} />}
          </div>
        </header>
        {body}
      </div>
      <div className="org-editor-mobile-nav">
        <MobileBottomNav />
      </div>
    </div>
  );
};

const OrganizationMeta = ({ organization }: { organization: OrganizationV2 }) => {
  const { t } = useTranslation();
  return (
    <div className="org-editor-meta">
      {organization.createdAt && (
        <span className="org-editor-meta-item">
          <span className="material-symbols-outlined" aria-hidden="true">event</span>
          {formatDate(organization.createdAt)}
        </span>
      )}
      <span className="org-editor-meta-item">
        <span className="material-symbols-outlined" aria-hidden="true">ev_station</span>
        {t('dashboard.organizations.stationsCount', { count: organization.stationCount })}
      </span>
      <span
        className={`org-editor-pill ${organization.hasMonobankToken ? 'is-on' : 'is-off'}`}
      >
        Monobank ·{' '}
        {organization.hasMonobankToken
          ? t('dashboard.organizations.connected')
          : t('dashboard.organizations.notConnected')}
      </span>
    </div>
  );
};

interface OrganizationFormProps {
  organization: OrganizationV2 | null; // null = create
  onDone: () => void;
}

const OrganizationForm = ({ organization, onDone }: OrganizationFormProps) => {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const createMutation = useCreateOrganizationMutation();
  const updateMutation = useUpdateOrganizationMutation();
  const mutation = organization ? updateMutation : createMutation;

  const [name, setName] = useState(organization?.name ?? '');
  const [nameTouched, setNameTouched] = useState(false);
  const [owner, setOwner] = useState<OwnerCandidate | null>(organization?.owner ?? null);

  const trimmedName = name.trim();
  const nameTooShort = trimmedName.length < ORGANIZATION_NAME_MIN_LENGTH;
  const previousOwner = organization?.owner ?? null;
  const ownerChanged = !!owner && owner.id !== previousOwner?.id;
  const isDirty = organization
    ? trimmedName !== organization.name || ownerChanged
    : trimmedName.length > 0 || owner !== null;
  const canSubmit = !nameTooShort && owner !== null && isDirty && !mutation.isPending;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setNameTouched(true);
    if (!canSubmit || !owner) {
      return;
    }
    try {
      if (organization) {
        await updateMutation.mutateAsync({
          id: organization.id,
          payload: {
            name: trimmedName,
            ownerUserId: ownerChanged ? owner.id : undefined,
          },
        });
      } else {
        await createMutation.mutateAsync({ name: trimmedName, ownerUserId: owner.id });
      }
      onDone();
    } catch {
      // Rendered from mutation.error below.
    }
  };

  const errorMessage = mutation.error
    ? parseApiError(mutation.error, t('dashboard.organizations.saveFailed')).message
    : undefined;

  return (
    <form className="org-editor-layout" onSubmit={handleSubmit} noValidate>
      <div className="org-editor-main">
        <section className="org-panel" aria-labelledby="org-details-title">
          <div className="org-panel-head">
            <h2 id="org-details-title" className="org-panel-title">
              {t('dashboard.organizations.detailsTitle')}
            </h2>
            <p className="org-panel-hint">{t('dashboard.organizations.detailsHint')}</p>
          </div>
          <div className="org-field">
            <div className="org-field-row">
              <label htmlFor="organization-name" className="org-field-label">
                {t('dashboard.organizations.nameLabel')}
              </label>
              <span className="org-field-counter" aria-hidden="true">
                {name.length}/{ORGANIZATION_NAME_MAX_LENGTH}
              </span>
            </div>
            <input
              id="organization-name"
              type="text"
              className={`org-input${nameTouched && nameTooShort ? ' is-invalid' : ''}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setNameTouched(true)}
              maxLength={ORGANIZATION_NAME_MAX_LENGTH}
              placeholder={t('dashboard.organizations.namePlaceholder')}
              disabled={mutation.isPending}
              aria-invalid={nameTouched && nameTooShort}
              autoFocus={!organization}
            />
            {nameTouched && nameTooShort && (
              <p className="org-field-error">
                {t('dashboard.organizations.nameTooShort', {
                  min: ORGANIZATION_NAME_MIN_LENGTH,
                })}
              </p>
            )}
          </div>
        </section>

        <section className="org-panel" aria-labelledby="org-owner-title">
          <div className="org-panel-head">
            <h2 id="org-owner-title" className="org-panel-title">
              {t('dashboard.organizations.ownerLabel')}
            </h2>
            <p className="org-panel-hint">{t('dashboard.organizations.ownerHint')}</p>
          </div>
          <OwnerUserSearch value={owner} onChange={setOwner} disabled={mutation.isPending} />
          {organization && ownerChanged && previousOwner && (
            <p className="org-callout" role="status">
              <span className="material-symbols-outlined" aria-hidden="true">swap_horiz</span>
              {t('dashboard.organizations.ownerChangeNote', {
                from: formatOwnerLabel(previousOwner),
                to: formatOwnerLabel(owner),
              })}
            </p>
          )}
        </section>
      </div>

      {organization && (
        <aside className="org-editor-side">
          <section className="org-panel" aria-labelledby="org-manage-title">
            <h2 id="org-manage-title" className="org-panel-title">
              {t('dashboard.organizations.manageTitle')}
            </h2>
            <nav className="org-links">
              <Link
                className="org-link"
                to={`/${ROUTES.DASHBOARD}/organizations/${organization.id}/stations`}
              >
                <span className="material-symbols-outlined" aria-hidden="true">ev_station</span>
                <span className="org-link-label">{t('dashboard.organizations.columnStations')}</span>
                <span className="org-link-count">{organization.stationCount}</span>
                <span className="material-symbols-outlined org-link-chevron" aria-hidden="true">
                  chevron_right
                </span>
              </Link>
              {has('organizations.manage') && (
                <Link className="org-link" to={organizationTeamPath(organization.id)}>
                  <span className="material-symbols-outlined" aria-hidden="true">groups</span>
                  <span className="org-link-label">{t('dashboard.organizations.teamAction')}</span>
                  <span className="material-symbols-outlined org-link-chevron" aria-hidden="true">
                    chevron_right
                  </span>
                </Link>
              )}
              <Link className="org-link" to={organizationPremiumPath(organization.id)}>
                <span className="material-symbols-outlined" aria-hidden="true">
                  workspace_premium
                </span>
                <span className="org-link-label">{t('dashboard.premium.orgTitle')}</span>
                <span className="material-symbols-outlined org-link-chevron" aria-hidden="true">
                  chevron_right
                </span>
              </Link>
            </nav>
          </section>
        </aside>
      )}

      <footer className="org-editor-actions">
        {errorMessage && (
          <p className="org-editor-error" role="alert">
            <span className="material-symbols-outlined" aria-hidden="true">error</span>
            {errorMessage}
          </p>
        )}
        <div className="org-editor-buttons">
          <button
            type="button"
            className="org-btn"
            onClick={onDone}
            disabled={mutation.isPending}
          >
            {t('dashboard.organizations.cancel')}
          </button>
          <button type="submit" className="org-btn org-btn-primary" disabled={!canSubmit}>
            <span className="material-symbols-outlined" aria-hidden="true">
              {organization ? 'check' : 'add'}
            </span>
            {mutation.isPending
              ? t('dashboard.organizations.saving')
              : organization
                ? t('dashboard.organizations.saveChanges')
                : t('dashboard.organizations.createAction')}
          </button>
        </div>
      </footer>
    </form>
  );
};

export default OrganizationEditor;
